import React, { Suspense } from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MwSpinner from './shared/MwSpinner';
import MwLoadingOverlay from './shared/MwLoadingOverlay';
import lazyPage from './shared/lazyPage';
import { CONTRACTS_PAGES } from './constants/routes';
import useContractsPaths from './hooks/useContractsPaths';
import { contractsMenu } from './constants/menu';

const ContractsDashboard = lazyPage(() => import(/* webpackPrefetch: true */ './pages/ContractsDashboard'));
const ManageContracts = lazyPage(() => import(/* webpackPrefetch: true */ './pages/ManageContracts'));
const ContractForm = lazyPage(() => import(/* webpackPrefetch: true */ './pages/ContractForm'));
const ShowContract = lazyPage(() => import(/* webpackPrefetch: true */ './pages/ShowContract'));
const ContractsSettings = lazyPage(() => import('./pages/ContractsSettings'));

// Pages only an admin of the app can open (same rule as RequireNonUser in the sales app).
const AdminOnly = ({ roleLoaded, isAdmin, children }) => {
    const paths = useContractsPaths();
    if (!roleLoaded) return <MwSpinner />;
    if (!isAdmin) return <Navigate to={paths.dashboard} replace />;
    return children;
};

// The module's own menu (dashboard, contracts, new contract, settings), for hosts whose side menu has a
// single entry for the module (apps-web-absat-v2).
const ModuleNav = ({ isAdmin }) => {
    const { t } = useTranslation('contracts');
    const paths = useContractsPaths();
    return (
        <nav className='ct-module-nav' aria-label={t('app.title')}>
            {contractsMenu.filter((item) => !item.adminOnly || isAdmin).map(({ path, labelKey, Icon, end }) => (
                <NavLink key={path || 'home'} end={end} to={path ? `${paths.base}/${path}` : paths.base}
                    className={({ isActive }) => `ct-module-nav-item ${isActive ? 'is-active' : ''}`}>
                    <Icon size={17} />{t(labelKey)}
                </NavLink>
            ))}
        </nav>
    );
};

/**
 * The whole contracts module, mounted by the host app on one route:
 *   <Route path=":tenant/contracts/*" element={<ContractsModule isAdmin={...} roleLoaded={...} />} />
 * - isAdmin / roleLoaded: the user's role in the app (settings are for admins only). Without them every user is admin.
 * - withToaster: false when the host already renders a react-hot-toast <Toaster />.
 * - withNav: the module's own menu on top; false when the host shows `contractsMenu` in its side menu.
 * The `.ct-root` wrapper scopes the module's styles (shared/module-base.css, contracts.css).
 */
const ContractsModule = ({ isAdmin = true, roleLoaded = true, withToaster = true, withNav = true }) => {
    const { i18n } = useTranslation('contracts');
    const dir = i18n.dir();
    const settings = <AdminOnly roleLoaded={roleLoaded} isAdmin={isAdmin}><ContractsSettings /></AdminOnly>;

    return (
        <div className='ct-root' dir={dir}>
            {withToaster && (
                <Toaster position='top-center' toastOptions={{ style: { direction: dir, fontFamily: 'Cairo, sans-serif', fontSize: 13 } }} />
            )}
            {withNav && <ModuleNav isAdmin={roleLoaded && isAdmin} />}
            <Suspense fallback={<div className='relative min-h-[60vh]'><MwLoadingOverlay /></div>}>
                <Routes>
                    <Route index element={<ContractsDashboard />} />
                    <Route path={CONTRACTS_PAGES.list} element={<ManageContracts />} />
                    <Route path={CONTRACTS_PAGES.create} element={<ContractForm />} />
                    <Route path={CONTRACTS_PAGES.edit} element={<ContractForm />} />
                    <Route path={CONTRACTS_PAGES.show} element={<ShowContract />} />
                    <Route path={CONTRACTS_PAGES.settings} element={settings} />
                    <Route path='*' element={<Navigate to='.' replace />} />
                </Routes>
            </Suspense>
        </div>
    );
};

export default ContractsModule;
